/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// sweetalert2 components
import Swal from "sweetalert2";

/**
 * Show a configurable confirmation dialog
 * @param {Object} options - Alert options
 * @param {string} [options.title="Are you sure?"] - Alert title
 * @param {string} [options.text="You won't be able to revert this!"] - Alert description text
 * @param {string} [options.icon="warning"] - Alert icon (warning, error, success, info, question)
 * @param {string} [options.confirmButtonText="Yes, proceed!"] - Text for confirm button
 * @param {string} [options.cancelButtonText="Cancel"] - Text for cancel button
 * @param {string} [options.successTitle="Success!"] - Title shown after successful confirmation
 * @param {string} [options.successText="Operation completed successfully."] - Text shown after successful confirmation
 * @param {string} [options.successIcon="success"] - Icon shown after successful confirmation
 * @param {Function} [options.onConfirm=null] - Function to execute on confirmation
 * @param {Function} [options.onCancel=null] - Function to execute on cancellation
 * @returns {Promise<boolean>} - Resolves with true if confirmed, false otherwise
 */
export const showConfirmationAlert = async ({
  title = "Eliminare?",
  text = "I dati eliminati non potranno più essere recuperati",
  icon = "warning",
  confirmButtonText = "Si, procedi!",
  cancelButtonText = "Annulla",
  successTitle = "Fatto!",
  successText = "Operazione completata con successo.",
  successIcon = "success",
  onConfirm = null,
  onCancel = null,
}) => {
  const newSwal = Swal.mixin({
    customClass: {
      confirmButton: "button button-success",
      cancelButton: "button button-error",
    },
    buttonsStyling: false,
  });

  try {
    const result = await newSwal.fire({
      title,
      text,
      icon,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
    });

    if (result.isConfirmed) {
      // Execute the onConfirm callback if provided
      if (typeof onConfirm === "function") {
        try {
          await onConfirm();

          // Show success message
          await Swal.fire({
            title: successTitle,
            text: successText,
            icon: successIcon,
          });

          return true; // Return true to indicate confirmation was successful
        } catch (error) {
          console.error("Error in confirmation callback:", error);

          // Show error message
          await Swal.fire({
            title: "Error",
            text: "Something went wrong during the operation. Please try again.",
            icon: "error",
          });

          return false; // Return false to indicate there was an error
        }
      } else {
        // If no onConfirm callback, just show success
        await Swal.fire({
          title: successTitle,
          text: successText,
          icon: successIcon,
        });

        return true;
      }
    } else if (result.isDismissed) {
      // Execute the onCancel callback if provided
      if (typeof onCancel === "function") {
        await onCancel();
      }

      return false; // Return false to indicate cancellation
    }

    return false;
  } catch (error) {
    console.error("Error in confirmation alert:", error);

    // Show error message
    await Swal.fire({
      title: "Error",
      text: "Something went wrong with the confirmation dialog. Please try again.",
      icon: "error",
    });

    return false;
  }
};

export default showConfirmationAlert;
